import useGetPaginatedLeases from "~/hooks/leases/useGetPaginatedLeases";
import useSingleOrganization from "~/hooks/organizations/useSingleOrganization";
import useGetPaginatedPermissions from "~/hooks/permissions/useGetPaginatedPermissions";
import useGetPaginatedProperties from "~/hooks/properties/useGetPaginatedProperties";
import useGetPaginatedRecurringInvoices from "~/hooks/recurringInvoices/useGetPaginatedRecurringInvoices";

import useSingleAccount from "./accounts/useSingleAccount";

const useCheckAccountSetup = () => {
  const {
    data: organizationData,
    isLoading: isOrganizationLoading,
    updateItem: updateOrganization,
  } = useSingleOrganization();

  const updateSkippedSettings = (skippedData: { [key: string]: boolean }) => {
    updateOrganization({
      setup: {
        ...organizationData?.setup,
        ...skippedData,
      },
    });
  };

  const { data: propertiesData, isLoading: isPropertiesLoading } =
    useGetPaginatedProperties({ depth: 1, filterOrganization: true });
  const { data: leasesData, isLoading: isLeasesLoading } =
    useGetPaginatedLeases({ depth: 1, filterOrganization: true });
  const { data: permissionsData, isLoading: isPermissionsLoading } =
    useGetPaginatedPermissions({ role: "tenant" });
  const { data: recurringInvoicesData, isLoading: isRecurringInvoicesLoading } =
    useGetPaginatedRecurringInvoices({ depth: 1, filterOrganization: true });

  const operatingAccount =
    typeof organizationData?.accounts?.operatingAccount === "object"
      ? (organizationData.accounts.operatingAccount?.id ?? null)
      : organizationData?.accounts?.operatingAccount;

  const { data: accountData, isLoading: isAccountLoading } = useSingleAccount({
    id: operatingAccount,
  });

  // Setup booleans.
  const settingsSkipped = organizationData?.setup;

  const hasUserVerifiedEmail =
    typeof organizationData?.owner === "object" &&
    organizationData?.owner !== null
      ? Boolean((organizationData.owner as { verified?: boolean }).verified)
      : false;

  // Is business set up?
  const organizationHasAddress = Boolean(organizationData?.address?.line1);
  const organizationHasType = Boolean(organizationData?.organizationType);

  // Is stripe set up?
  const stripeConnected = accountData?.stripe?.enabled;
  const stripePayoutsEnabled =
    typeof accountData?.stripe?.stripeAccount === "object" &&
    accountData?.stripe?.stripeAccount?.payoutsEnabled;
  const stripeChargeEnabled =
    typeof accountData?.stripe?.stripeAccount === "object" &&
    accountData?.stripe?.stripeAccount?.chargesEnabled;

  let stripe = false as "pending" | "complete" | false;
  if (stripeConnected && stripePayoutsEnabled && stripeChargeEnabled) {
    stripe = "complete";
  }
  if (stripeConnected && !stripePayoutsEnabled && !stripeChargeEnabled) {
    stripe = "pending";
  }

  if (settingsSkipped?.paymentsSkipped) {
    stripe = "complete";
  }

  // Has a property been added?
  const properties = propertiesData?.length > 0 ? "complete" : false;

  // Has a lease been added?
  const leases = leasesData?.length > 0 ? "complete" : false;

  // Has a tenant had a permission adding.
  const tenants = permissionsData?.length > 0 ? "complete" : false;

  // Has a recurring invoice been added?
  const recurringInvoices =
    recurringInvoicesData?.length > 0 ? "complete" : false;

  const data = {
    userVerifiedEmail: hasUserVerifiedEmail ? "complete" : false,
    businessInfo:
      organizationHasAddress && organizationHasType
        ? "complete"
        : settingsSkipped?.ownerInformationSkipped
          ? "complete"
          : false,
    payments: stripe,
    properties,
    leases,
    tenants,
    rent: recurringInvoices,
    setupSkipped: settingsSkipped?.setupSkipped,
  };

  return {
    data,
    updateSkippedSettings,
    isLoading:
      isOrganizationLoading ||
      isAccountLoading ||
      isPropertiesLoading ||
      isLeasesLoading ||
      isPermissionsLoading ||
      isRecurringInvoicesLoading,
  };
};

export default useCheckAccountSetup;
